<script setup lang="ts">
import { ref } from "vue";
import type { Team } from "@shared/types";
import Flag from "../common/Flag.vue";

const props = defineProps<{
  teams: { pot1: Team; pot2: Team; pot3: Team };
  saving: boolean;
}>();

const emit = defineEmits<{
  (e: "save", name: string): void;
  (e: "cancel"): void;
}>();

const name = ref("");
const canSave = () => name.value.trim().length >= 1 && name.value.trim().length <= 40 && !props.saving;
</script>

<template>
  <section class="mt-6 rounded-2xl ring-1 ring-amber-200 bg-white shadow-lg p-6 max-w-md mx-auto">
    <h3 class="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Your draw</h3>
    <ul class="space-y-2.5 mb-5">
      <li class="flex items-center gap-3"><Flag :code="teams.pot1.flag_code" :name="teams.pot1.name" /><span class="font-medium text-slate-800">{{ teams.pot1.name }}</span></li>
      <li class="flex items-center gap-3"><Flag :code="teams.pot2.flag_code" :name="teams.pot2.name" /><span class="font-medium text-slate-800">{{ teams.pot2.name }}</span></li>
      <li class="flex items-center gap-3"><Flag :code="teams.pot3.flag_code" :name="teams.pot3.name" /><span class="font-medium text-pot3">{{ teams.pot3.name }}</span> <span class="text-xs text-slate-400">×2</span></li>
    </ul>
    <label class="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1" for="entry-name">Your name</label>
    <input
      id="entry-name"
      v-model="name"
      type="text"
      maxlength="40"
      placeholder="e.g. Alice"
      class="w-full mb-4 rounded-md bg-white border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:outline-none focus:border-pot1 focus:ring-2 focus:ring-pot1/30"
      :disabled="saving"
    />
    <div class="flex gap-2 justify-end">
      <button type="button" class="px-4 py-2 rounded-md text-slate-500 hover:text-slate-800" @click="emit('cancel')" :disabled="saving">Cancel</button>
      <button
        type="button"
        @click="canSave() && emit('save', name.trim())"
        :disabled="!canSave()"
        class="px-5 py-2 rounded-md bg-gradient-to-b from-amber-300 to-pot1 text-amber-950 font-bold shadow-sm disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-500 disabled:shadow-none"
      >{{ saving ? "Saving…" : "Save Entry" }}</button>
    </div>
  </section>
</template>
